import React, { useEffect, useRef, useState } from "react"
import types from "prop-types"
import { useSelector } from "react-redux"
import { formSubmissionData } from "reduxSlices/formSubmissionSlice"
import * as API from "services/api"
import FormSubmissionFormContext from "../FormSubmissionFormContext"

const FormSubmissionFormContextProvider = ({ children, formSlug, submittedFormVersionId }) => {
  const { isClosed } = useSelector(formSubmissionData)

  const [form, setForm] = useState({})
  const [currentFormVersion, setCurrentFormVersion] = useState({})
  const [submittedFormVersion, setSubmittedFormVersion] = useState({})

  // The current version of the form is only needed once a user begins editing the
  // submission, so it is requested at that point rather than when the page loads.
  // The request is held onto, so that the version is only ever requested once.
  const currentFormVersionRequest = useRef(null)

  const loadCurrentFormVersion = () => {
    if (!currentFormVersionRequest.current) {
      currentFormVersionRequest.current = API.currentFormVersion({ formSlug: form.slug }).then((response) => {
        if (response.ok) {
          setCurrentFormVersion(response.data)
        } else {
          // Forget the failed request, so that a later attempt requests the version again.
          currentFormVersionRequest.current = null
        }

        return response
      })
    }

    return currentFormVersionRequest.current
  }

  // A submission's form type can be changed without reloading the page, which
  // changes the form in state but not the formSlug prop.  So the request for a
  // previous form's current version is forgotten when the form in state changes.
  useEffect(() => {
    currentFormVersionRequest.current = null
  }, [form.slug])

  // A submission is edited using either the version of the form that was submitted
  // or the current version of the form.
  const [formVersionIdUsedInEdit, setFormVersionIdUsedInEdit] = useState(null)
  const formVersionUsedInEdit = [submittedFormVersion, currentFormVersion]
    .find(({ id }) => id === formVersionIdUsedInEdit) || {}

  const [editMode, setEditMode] = useState(false)

  const closeEditMode = () => {
    setEditMode(false)
    setFormVersionIdUsedInEdit(null)
  }

  const openEditMode = (formVersionId) => {
    if (isClosed) return

    setFormVersionIdUsedInEdit(formVersionId)
    setEditMode(true)
  }

  useEffect(() => {
    const loadData = async () => {
      const formResponse = await API.formForFormSubmission({ formSlug })
      const submittedFormVersionResponse = await API.formVersion({
        formSlug,
        formVersionId: submittedFormVersionId,
      })

      if (formResponse.ok) {
        // The form's current version id and sections are not needed here,
        // since the current version is requested when a user begins editing.
        const { currentVersionId, sections, ...formData } = formResponse.data

        setForm(formData)
      }

      if (submittedFormVersionResponse.ok) {
        setSubmittedFormVersion(submittedFormVersionResponse.data)
      }
    }

    loadData()
  }, [formSlug, submittedFormVersionId])

  // Object exposed to context consumers
  const contextConsumerValue = {
    closeEditMode,
    currentFormVersion,
    editMode,
    form,
    formVersionUsedInEdit,
    loadCurrentFormVersion,
    openEditMode,
    setForm,
    setSubmittedFormVersion,
    submittedFormVersion,
  }

  return (
    <FormSubmissionFormContext.Provider value={contextConsumerValue}>
      {children}
    </FormSubmissionFormContext.Provider>
  )
}

FormSubmissionFormContextProvider.propTypes = {
  children: types.node.isRequired,
  formSlug: types.string.isRequired,
  submittedFormVersionId: types.number.isRequired,
}

export default FormSubmissionFormContextProvider
